import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import {
  AppointmentDto,
  AppointmentSlotDto,
  AppointmentSlotStatus,
  AppointmentStatus,
  DiscussionDto,
  PropertyApplicationDto,
  PropertyApplicationPendingActionType,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { AuraCountdownComponent } from '@workspace/ng-aura';
import { PropertyExploreCardComponent } from '../../../property/components/property-explore-card';

@Component({
  selector: 'etxe-discussion-property-sidebar',
  imports: [
    CommonModule,
    DatePipe,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    PropertyExploreCardComponent,
    AuraCountdownComponent,
  ],
  templateUrl: './discussion-property-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionPropertySidebarComponent {
  readonly property = input.required<PropertyDto>();
  readonly activeDiscussion = input<DiscussionDto | null>(null);
  readonly isOwner = input<boolean>(false);
  readonly appointment = input<AppointmentDto | null>(null);
  readonly application = input<PropertyApplicationDto | null>(null);

  readonly deadline = computed<Date | string | null>(() => {
    const app = this.application();
    if (app?.pendingAction?.deadline) {
      return app.pendingAction.deadline;
    }
    const appt = this.appointment();
    if (!appt) return null;
    if (appt.updatedAt) {
      return new Date(new Date(appt.updatedAt).getTime() + 24 * 60 * 60 * 1000);
    }
    if (appt.createdAt) {
      return new Date(new Date(appt.createdAt).getTime() + 24 * 60 * 60 * 1000);
    }
    return null;
  });

  readonly isOfferSubmitted = computed(() => {
    return this.application()?.status === PropertyApplicationStatus.OFFER_SUBMITTED;
  });

  readonly isMandateSelection = computed(() => {
    return (
      this.application()?.pendingAction?.actionType === PropertyApplicationPendingActionType.MANDATE_SELECTION
    );
  });

  readonly countdownPrefix = computed(() => {
    if (this.isMandateSelection() || this.isOfferSubmitted()) {
      return this.isOwner() ? 'Fin de la délibération dans :' : 'Délibération du propriétaire dans :';
    }
    return this.isOwner()
      ? "Délai pour l'avis de valeur et l'offre finale :"
      : 'Temps restant pour votre avis de valeur et offre finale :';
  });

  readonly openVisitModal = output<void>();
  readonly proposeVisit = output<void>();
  readonly confirmSlot = output<string>();
  readonly requestReschedule = output<string | undefined>();
  readonly markAsVisited = output<void>();
  readonly adjustFinalOffer = output<void>();

  readonly isProposed = computed(() => {
    return this.appointment()?.status === AppointmentStatus.PROPOSED;
  });

  readonly isRescheduled = computed(() => {
    return this.appointment()?.status === AppointmentStatus.RESCHEDULED;
  });

  readonly isConfirmed = computed(() => {
    return this.appointment()?.status === AppointmentStatus.CONFIRMED;
  });

  readonly isCompleted = computed(() => {
    return this.appointment()?.status === AppointmentStatus.COMPLETED;
  });

  readonly isCancelled = computed(() => {
    return this.appointment()?.status === AppointmentStatus.CANCELLED;
  });

  readonly rescheduleReason = computed<string | null>(() => {
    const declined = (this.appointment()?.proposedSlots || []).find((s) => s.declineReason);
    return declined?.declineReason || this.appointment()?.notes || null;
  });

  readonly proposedSlotsCount = computed(() => {
    return (this.appointment()?.proposedSlots || []).filter(
      (s) => s.status === AppointmentSlotStatus.PROPOSED,
    ).length;
  });

  readonly confirmedSlot = computed<AppointmentSlotDto | null>(() => {
    return this.appointment()?.confirmedSlot || null;
  });

  readonly isVisitPassed = computed<boolean>(() => {
    const slot = this.confirmedSlot();
    if (!slot) return false;
    const end = slot.endAt || slot.startAt;
    return new Date().getTime() >= new Date(end).getTime();
  });

  onProposeVisit(): void {
    this.proposeVisit.emit();
  }

  onConfirmSlot(slotId: string): void {
    this.confirmSlot.emit(slotId);
  }

  onRequestReschedule(reason?: string): void {
    this.requestReschedule.emit(reason);
  }

  onMarkAsVisited(): void {
    this.markAsVisited.emit();
  }

  onAdjustFinalOffer(): void {
    this.adjustFinalOffer.emit();
  }
}
